import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * The product image gallery.
 *
 * The stage is a scroll-snap container, so swiping on a phone and dragging a
 * trackpad are the browser's job rather than a drag implementation of ours.
 * Thumbnails scroll it; an IntersectionObserver reports back which slide
 * landed, which keeps the active thumbnail correct whichever way the shopper
 * moved.
 *
 * @extends {Component}
 */
class MediaGalleryComponent extends Component {
  requiredRefs = ['stage'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#observe();
    this.#openOnActive();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * Start on the shot for the variant the page opened with.
   *
   * The stage is a scroll container, so with no instruction it starts at its
   * first slide, and the first slide is whichever media happens to lead the
   * product. Once the rail is filtered to one stone plus the shots that belong
   * to no stone, that lead is usually a bench shot, so the page opened on the
   * wrong picture.
   */
  #openOnActive() {
    const active = this.querySelector('[role="tab"][aria-selected="true"]');
    const id = active?.getAttribute('data-media-id');
    if (id) this.show(id, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  /**
   * @param {Event} event
   * @param {Element} element
   */
  select = (event, element) => {
    event.preventDefault();
    const id = element.getAttribute('data-media-id');
    if (id) this.show(id, true);
  };

  /**
   * @param {string} mediaId
   * @param {boolean} [smooth]
   */
  show(mediaId, smooth = false) {
    const stage = /** @type {HTMLElement} */ (this.refs.stage);
    const slide = stage.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    if (!(slide instanceof HTMLElement)) return;

    // Measured rather than derived from offsetLeft, which is only comparable
    // between the two when they share an offsetParent. Positioning the stage
    // for any reason would silently break that assumption.
    const delta = slide.getBoundingClientRect().left - stage.getBoundingClientRect().left;

    /*
     * Smooth only over a short hop. Sliding two frames tells the shopper the
     * gallery moved and which way; racing through nine of them on the way to
     * the tenth just spins the picture, and a product in eight stones is
     * exactly where that happens. Past a slide and a half it jumps.
     */
    const near = Math.abs(delta) <= slide.getBoundingClientRect().width * 1.5;
    const animate = smooth && near && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    stage.scrollTo({
      left: stage.scrollLeft + delta,
      behavior: animate ? 'smooth' : 'auto',
    });

    this.#markActive(mediaId);
  }

  /**
   * @param {string} mediaId
   */
  #markActive(mediaId) {
    for (const thumb of this.querySelectorAll('[data-media-id][role="tab"]')) {
      const active = thumb.getAttribute('data-media-id') === mediaId;
      thumb.setAttribute('aria-selected', String(active));
      // Roving tabindex: one stop for the whole strip, then arrow keys within.
      thumb.setAttribute('tabindex', active ? '0' : '-1');
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  navigate = (event) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;

    event.preventDefault();

    const thumbs = Array.from(this.querySelectorAll('[data-media-id][role="tab"]'));
    const current = thumbs.findIndex((thumb) => thumb.getAttribute('aria-selected') === 'true');
    const next = thumbs[(current + step + thumbs.length) % thumbs.length];

    if (next instanceof HTMLElement) {
      const id = next.getAttribute('data-media-id');
      if (id) this.show(id, true);
      next.focus();
    }
  };

  #observe() {
    this.#observer?.disconnect();

    const stage = this.refs.stage;
    if (!(stage instanceof HTMLElement)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = entry.target.getAttribute('data-media-id');
          if (id) this.#markActive(id);
        }
      },
      { root: stage, threshold: 0.6 }
    );

    for (const slide of stage.querySelectorAll('[data-media-id]')) this.#observer.observe(slide);
  }

  /**
   * Show only the shots that belong to the chosen variant, plus the ones that
   * belong to no variant at all. Liquid renders the same state for the variant
   * the page opens on, so nothing flashes before this runs.
   *
   * @param {string|number} variantId
   */
  #filter(variantId) {
    const current = String(variantId);
    for (const el of this.querySelectorAll('[data-variant-media]')) {
      /** @type {HTMLElement} */ (el).hidden = el.getAttribute('data-variant-media') !== current;
    }

    // Force the layout the hidden slides just invalidated. show() measures
    // where a slide sits, and without this it would measure the old positions
    // and scroll to where the picture used to be. A rAF would also work, until
    // the tab is backgrounded and rAF stops firing.
    void this.offsetWidth;
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const variant = /** @type {CustomEvent} */ (event).detail?.variant;
    if (variant?.id != null) this.#filter(variant.id);

    const mediaId = variant?.featured_media?.id;

    // Only jump when the variant actually has its own image. Scrolling back to
    // the first shot every time an option changes loses the shopper's place
    // for no gain.
    //
    // Never animated. Picking a stone is a content swap, not a move through
    // the gallery, and the shopper is looking at the swatch rather than at the
    // photograph when it happens.
    if (mediaId) this.show(String(mediaId), false);
  };
}

if (!customElements.get('media-gallery-component')) {
  customElements.define('media-gallery-component', MediaGalleryComponent);
}
